import { Avatar } from '@seek/avatar';
import { SeekPassIconVerified } from '@seek/certsy-ui';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  Column,
  Columns,
  Heading,
  IconLocation,
  IconMail,
  IconPhone,
  IconEdit,
  Stack,
  Text,
  TooltipRenderer,
  useResponsiveValue,
} from 'braid-design-system';

import { useConfig } from 'src/context/config';
import { useEnhancedHubble } from 'src/hooks/useEnhancedHubble';
import { useGetProfileAvatar } from 'src/hooks/useGetProfileAvatar';
import { formatMobile } from 'src/modules/utils';
import { normalizeName } from 'src/utils';

import translations from './PersonalDetails.vocab';

import * as styles from './PersonalDetailsView.css';

interface Props {
  onEditClick: () => void;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  countryCallingCode: string;
  homeLocationShortDesc: string;
  verifiedIdentityName: string;
}

const PersonalDetailsView: React.FC<Props> = ({
  firstName,
  lastName,
  emailAddress,
  phoneNumber,
  countryCallingCode,
  homeLocationShortDesc,
  verifiedIdentityName,
  onEditClick,
}) => {
  const { t } = useTranslations(translations);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });
  const hubble = useEnhancedHubble();
  const { config } = useConfig();
  const { profileAvatar, loading } = useGetProfileAvatar();
  const image128Url = profileAvatar?.image128Url;

  const verifiedIdentityNameUpper = verifiedIdentityName
    ? normalizeName(verifiedIdentityName)
    : '';
  const nameUpper = normalizeName(`${firstName} ${lastName}`);
  const verifiedIdentityLabel =
    verifiedIdentityNameUpper === nameUpper
      ? t('Identity verified')
      : `${t('Identity verified as name')} ${verifiedIdentityName}`;

  const handleAvatarClick = () =>
    hubble.trackEvent('component_pressed', {
      component: {
        type: 'button',
        name: 'profile avatar',
      },
      displayLanguage: config.VOCAB_LANGUAGE,
      zone: config.ZONE,
    });

  return (
    <Box
      position="relative"
      padding="gutter"
      paddingBottom={{ mobile: 'large', tablet: 'gutter' }}
      background="brand"
      borderRadius="xlarge"
      overflow="hidden"
      className={styles.profileShapes}
    >
      <Box
        position={
          'relative' /** important to ensure content overlays background shapes */
        }
      >
        <Columns space="medium" alignY="top">
          <Column>
            <Columns space="medium" collapseBelow="tablet" alignY="top">
              <Column width="content">
                <Box onClick={handleAvatarClick} maxWidth="content">
                  <Avatar
                    variant={image128Url ? 'icon' : 'initials'}
                    size={isMobile ? 'large' : 'xlarge'}
                    photoUrl={image128Url}
                    name={firstName}
                    border
                    data-testid="profile-avatar"
                    loading={loading}
                  />
                </Box>
              </Column>
              <Column>
                <Stack space="medium">
                  <Box paddingTop={{ tablet: 'xxsmall' }}>
                    {verifiedIdentityName ? (
                      <TooltipRenderer
                        tooltip={<Text>{verifiedIdentityLabel}</Text>}
                      >
                        {({ triggerProps }) => (
                          <Heading level="4">
                            {firstName} {lastName}&nbsp;
                            <Box
                              display="inline"
                              aria-label={verifiedIdentityLabel}
                              data-testid="verified-identity-icon"
                              style={{
                                fontSize: '16px',
                                verticalAlign: 'top',
                              }}
                              {...triggerProps}
                            >
                              <SeekPassIconVerified darkMode />
                            </Box>
                          </Heading>
                        )}
                      </TooltipRenderer>
                    ) : (
                      <Heading level="4">
                        {firstName} {lastName}
                      </Heading>
                    )}
                  </Box>

                  <Stack space="small">
                    <Text icon={<IconLocation tone="secondary" />} size="small">
                      {homeLocationShortDesc}
                    </Text>
                    <Text
                      icon={<IconPhone tone="secondary" />}
                      size="small"
                      data={{ 'hj-suppress': 'true' }}
                    >
                      {`${countryCallingCode} ${formatMobile(phoneNumber)}`}
                    </Text>
                    <Text
                      icon={<IconMail tone="secondary" />}
                      size="small"
                      maxLines={1}
                    >
                      {emailAddress}
                    </Text>
                  </Stack>
                </Stack>
              </Column>
            </Columns>
          </Column>
          <Column width="content">
            <ButtonIcon
              size="standard"
              variant="soft"
              tone="neutral"
              icon={<IconEdit />}
              label={t('editPersonalDetails')}
              onClick={onEditClick}
              data={{
                testid: 'edit-personal-details',
              }}
            />
          </Column>
        </Columns>
      </Box>
    </Box>
  );
};

export default PersonalDetailsView;
